import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Form, Input, Button, Card, Typography, Alert, Space, Result } from 'antd'
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import api from '../services/api'

const { Title, Text } = Typography

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [email, setEmail] = useState('')
  const navigate = useNavigate()

  const onFinish = async (values) => {
    setLoading(true)
    setError('')

    try {
      await api.post('/auth/forgot-password', {
        email: values.email,
        user_type: 'admin'
      })

      setEmail(values.email)
      setSuccess(true)
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many password reset requests. Please try again later.')
      } else {
        setError(err.response?.data?.detail || 'An error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4"
        style={{
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Card
          className="w-full max-w-md shadow-2xl"
          bodyStyle={{ padding: window.innerWidth < 640 ? '1.5rem' : '2rem' }}
        >
          <Result
            status="success"
            title="Check Your Email"
            subTitle={
              <Space direction="vertical" size="small" className="w-full">
                <Text>
                  If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
                </Text>
                <Text type="secondary" className="text-sm">
                  The link will expire in 1 hour for security reasons.
                </Text>
              </Space>
            }
            extra={[
              <Button
                type="primary"
                key="login"
                onClick={() => navigate('/login')}
                icon={<ArrowLeftOutlined />}
              >
                Back to Login
              </Button>
            ]}
          />
        </Card>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4"
      style={{
        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Card
        className="w-full max-w-md shadow-2xl"
        bodyStyle={{ padding: window.innerWidth < 640 ? '1.5rem' : '2rem' }}
      >
        <Space direction="vertical" size="large" className="w-full">
          {/* Logo and Title */}
          <div className="text-center">
            <div className="flex flex-col items-center mb-6">
              <img
                src="/Icon.png"
                alt="ACIS"
                className="w-20 h-20 mb-3"
              />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-gray-900 mb-1">ACIS</span>
                <span className="text-sm text-gray-600">Alpha Centauri Investment Strategies Inc.</span>
              </div>
            </div>
            <Title level={2} className="mb-0">
              Reset Password
            </Title>
            <Text type="secondary">
              Enter your email to receive a password reset link
            </Text>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
            />
          )}

          {/* Forgot Password Form */}
          <Form
            name="forgot-password"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Email Address"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item className="mb-4">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                className="gradient-primary"
              >
                Send Reset Link
              </Button>
            </Form.Item>

            <Form.Item className="mb-0">
              <Link to="/login">
                <Button
                  type="link"
                  block
                  icon={<ArrowLeftOutlined />}
                  className="px-0"
                >
                  Back to Login
                </Button>
              </Link>
            </Form.Item>
          </Form>

          {/* Footer */}
          <div className="text-center text-gray-500 text-sm mt-4">
            <Text type="secondary">
              ACIS AI Platform - Admin Access Only
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  )
}

export default ForgotPassword
